import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus,
  Search,
  Bug,
  ShieldAlert,
  CheckCircle,
  Loader2,
  RefreshCw,
  Trash2,
  Pencil,
  ExternalLink,
  AlertCircle,
} from 'lucide-react';
import { vulnerabilitiesApi, assetsApi } from '../lib/api';
import { toast } from 'sonner';
import { cn, formatDate } from '../lib/utils';

const severities = [
  { value: 'critica', label: 'Crítica', color: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'alta', label: 'Alta', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { value: 'media', label: 'Média', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  { value: 'baixa', label: 'Baixa', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'informativa', label: 'Informativa', color: 'bg-slate-50 text-slate-700 border-slate-200' },
];

const statuses = [
  { value: 'nova', label: 'Nova', color: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'confirmada', label: 'Confirmada', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { value: 'em_remediacao', label: 'Em Remediação', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'remediada', label: 'Remediada', color: 'bg-green-50 text-green-700 border-green-200' },
  { value: 'aceite', label: 'Risco Aceite', color: 'bg-slate-50 text-slate-700 border-slate-200' },
  { value: 'falso_positivo', label: 'Falso Positivo', color: 'bg-slate-50 text-slate-500 border-slate-200' },
];

const scanners = [
  { value: 'nessus', label: 'Tenable Nessus' },
  { value: 'qualys', label: 'Qualys VMDR' },
  { value: 'openvas', label: 'OpenVAS / GVM' },
];

const initialFormData = {
  cve_id: '',
  title: '',
  description: '',
  severity: 'media',
  cvss_score: '',
  asset_id: '',
  affected_component: '',
  solution: '',
};

const getSeverityConfig = (sev) => severities.find((s) => s.value === sev) || severities[2];
const getStatusConfig = (st) => statuses.find((s) => s.value === st) || statuses[0];

export default function Vulnerabilities() {
  const [vulns, setVulns] = useState([]);
  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0, critical_open: 0, by_severity: {}, by_status: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [scannerFilter, setScannerFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedVuln, setSelectedVuln] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [showSync, setShowSync] = useState(false);
  const [syncScanner, setSyncScanner] = useState('nessus');
  const [syncCount, setSyncCount] = useState(3);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [severityFilter, statusFilter, scannerFilter]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (severityFilter) params.severity = severityFilter;
      if (statusFilter) params.status = statusFilter;
      if (scannerFilter) params.scanner_source = scannerFilter;

      const [vulnsRes, statsRes, assetsRes] = await Promise.all([
        vulnerabilitiesApi.getAll(params),
        vulnerabilitiesApi.getStats(),
        assetsApi.getAll({}),
      ]);
      setVulns(vulnsRes.data);
      setStats(statsRes.data);
      setAssets(assetsRes.data);
    } catch (err) {
      toast.error('Erro ao carregar vulnerabilidades');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('O título é obrigatório');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        cvss_score: formData.cvss_score ? parseFloat(formData.cvss_score) : null,
        asset_id: formData.asset_id || null,
        cve_id: formData.cve_id || null,
      };
      if (editing) {
        await vulnerabilitiesApi.update(editing.vulnerability_id, payload);
        toast.success('Vulnerabilidade atualizada');
      } else {
        await vulnerabilitiesApi.create(payload);
        toast.success('Vulnerabilidade registada');
      }
      setShowForm(false);
      setEditing(null);
      setFormData(initialFormData);
      loadData();
    } catch (err) {
      toast.error('Erro ao guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedVuln) return;
    try {
      await vulnerabilitiesApi.update(selectedVuln.vulnerability_id, { status: newStatus });
      toast.success('Estado atualizado');
      setShowStatusDialog(false);
      setSelectedVuln(null);
      loadData();
    } catch (err) {
      toast.error('Erro ao atualizar estado');
    }
  };

  const handleEdit = (vuln) => {
    setEditing(vuln);
    setFormData({
      cve_id: vuln.cve_id || '',
      title: vuln.title || '',
      description: vuln.description || '',
      severity: vuln.severity || 'media',
      cvss_score: vuln.cvss_score?.toString() || '',
      asset_id: vuln.asset_id || '',
      affected_component: vuln.affected_component || '',
      solution: vuln.solution || '',
    });
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!selectedVuln) return;
    try {
      await vulnerabilitiesApi.delete(selectedVuln.vulnerability_id);
      toast.success('Vulnerabilidade eliminada');
      setShowDelete(false);
      setSelectedVuln(null);
      loadData();
    } catch (err) {
      toast.error('Erro ao eliminar');
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const res = await vulnerabilitiesApi.syncScanner(syncScanner, syncCount);
      toast.success(`${res.data.created} vulnerabilidades importadas de ${syncScanner.toUpperCase()}`);
      setShowSync(false);
      loadData();
    } catch (err) {
      toast.error('Erro durante o sync com o scanner');
    } finally {
      setIsSyncing(false);
    }
  };

  const filtered = vulns.filter(
    (v) =>
      v.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.vulnerability_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (v.cve_id || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getAssetName = (assetId) => {
    if (!assetId) return null;
    const a = assets.find((x) => x.asset_id === assetId);
    return a ? a.name : assetId;
  };

  const headerContent = (
    <div className="flex items-center gap-2">
      <Button variant="outline" onClick={() => setShowSync(true)} data-testid="sync-scanner-btn">
        <RefreshCw className="h-4 w-4 mr-2" />
        Sync Scanner
      </Button>
      <Button
        onClick={() => {
          setEditing(null);
          setFormData(initialFormData);
          setShowForm(true);
        }}
        data-testid="new-vulnerability-btn"
      >
        <Plus className="h-4 w-4 mr-2" />
        Nova Vulnerabilidade
      </Button>
    </div>
  );

  if (isLoading) {
    return (
      <Layout title="Gestão de Vulnerabilidades">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Gestão de Vulnerabilidades" headerContent={headerContent}>
      <div className="space-y-6" data-testid="vulnerabilities-container">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-flat">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Bug className="h-8 w-8 text-slate-400" />
                <span className="text-2xl font-bold text-slate-900" data-testid="stat-total">{stats.total}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Total Registadas</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <ShieldAlert className="h-8 w-8 text-red-500" />
                <span className="text-2xl font-bold text-red-600" data-testid="stat-critical-open">{stats.critical_open}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Críticas em Aberto</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <AlertCircle className="h-8 w-8 text-orange-500" />
                <span className="text-2xl font-bold text-orange-600" data-testid="stat-open">{stats.open}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Em Aberto</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <CheckCircle className="h-8 w-8 text-green-500" />
                <span className="text-2xl font-bold text-green-600" data-testid="stat-remediated">{stats.by_status?.remediada || 0}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Remediadas</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="card-flat">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar por CVE, título ou ID..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-vulnerabilities-input"
                />
              </div>
              <Select value={severityFilter} onValueChange={(v) => setSeverityFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[160px]" data-testid="filter-severity">
                  <SelectValue placeholder="Severidade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {severities.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[160px]" data-testid="filter-status">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {statuses.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={scannerFilter} onValueChange={(v) => setScannerFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[160px]" data-testid="filter-scanner">
                  <SelectValue placeholder="Origem" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  {scanners.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* List */}
        {filtered.length === 0 ? (
          <Card className="card-flat">
            <CardContent className="p-8 text-center">
              <Bug className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Nenhuma vulnerabilidade encontrada</h3>
              <p className="text-sm text-slate-500 mb-4">
                {searchQuery || severityFilter || statusFilter || scannerFilter
                  ? 'Tente ajustar os filtros'
                  : 'Importe vulnerabilidades de um scanner ou registe manualmente'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((v) => {
              const sevCfg = getSeverityConfig(v.severity);
              const stCfg = getStatusConfig(v.status);
              const assetName = getAssetName(v.asset_id);
              return (
                <Card
                  key={v.vulnerability_id}
                  className="card-flat hover:shadow-sm transition-shadow"
                  data-testid={`vuln-${v.vulnerability_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={cn('h-12 w-12 rounded-lg flex items-center justify-center', sevCfg.color)}>
                        <Bug className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-mono text-xs text-slate-400">{v.vulnerability_id}</span>
                              {v.cve_id && (
                                <Badge variant="outline" className="text-xs font-mono bg-slate-50">
                                  {v.cve_id}
                                </Badge>
                              )}
                              <Badge variant="outline" className={cn('text-xs', sevCfg.color)}>
                                {sevCfg.label}
                              </Badge>
                              <Badge variant="outline" className={cn('text-xs', stCfg.color)}>
                                {stCfg.label}
                              </Badge>
                              {v.cvss_score != null && (
                                <Badge variant="outline" className="text-xs bg-slate-50">
                                  CVSS {v.cvss_score}
                                </Badge>
                              )}
                              {v.scanner_source && v.scanner_source !== 'manual' && (
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                  {v.scanner_source.toUpperCase()}
                                </Badge>
                              )}
                            </div>
                            <h3 className="font-medium text-slate-900 truncate">{v.title}</h3>
                            {v.affected_component && (
                              <p className="text-sm text-slate-500 mt-1">
                                Componente: <span className="font-mono">{v.affected_component}</span>
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedVuln(v);
                                setShowStatusDialog(true);
                              }}
                              data-testid={`change-status-${v.vulnerability_id}`}
                            >
                              Alterar Estado
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(v)} data-testid={`edit-${v.vulnerability_id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => {
                                setSelectedVuln(v);
                                setShowDelete(true);
                              }}
                              data-testid={`delete-${v.vulnerability_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        {v.description && (
                          <p className="text-sm text-slate-600 mt-2 line-clamp-2">{v.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500 flex-wrap">
                          {assetName && v.asset_id && (
                            <Link
                              to={`/assets/${v.asset_id}`}
                              className="text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <ExternalLink className="h-3 w-3" />
                              {assetName}
                            </Link>
                          )}
                          <span>Descoberta: {formatDate(v.discovered_at)}</span>
                          {v.remediated_at && (
                            <span className="text-green-600">Remediada: {formatDate(v.remediated_at)}</span>
                          )}
                          {v.references?.[0] && (
                            <a
                              href={v.references[0]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              NVD ↗
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Status Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar Estado</DialogTitle>
            <DialogDescription>
              Atualize o estado da vulnerabilidade {selectedVuln?.vulnerability_id}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            {statuses.map((s) => (
              <Button
                key={s.value}
                variant={selectedVuln?.status === s.value ? 'default' : 'outline'}
                className="justify-start"
                onClick={() => handleStatusChange(s.value)}
                data-testid={`status-option-${s.value}`}
              >
                {s.label}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Sync Scanner Dialog */}
      <Dialog open={showSync} onOpenChange={setShowSync}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sincronizar Scanner de Vulnerabilidades</DialogTitle>
            <DialogDescription>
              Importe vulnerabilidades do scanner selecionado. Os CVEs serão automaticamente mapeados a ativos existentes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Scanner</Label>
              <Select value={syncScanner} onValueChange={setSyncScanner}>
                <SelectTrigger data-testid="sync-scanner-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {scanners.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Número de vulnerabilidades a importar</Label>
              <Input
                type="number"
                min="1"
                max="5"
                value={syncCount}
                onChange={(e) => setSyncCount(parseInt(e.target.value) || 1)}
                data-testid="sync-count-input"
              />
            </div>
            <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-3">
              <strong>Nota:</strong> Esta funcionalidade é uma simulação. Em produção, a integração utilizará as credenciais reais do scanner configuradas em Definições.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSync(false)}>Cancelar</Button>
            <Button onClick={handleSync} disabled={isSyncing} data-testid="confirm-sync-btn">
              {isSyncing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <RefreshCw className="h-4 w-4 mr-2" />
              Iniciar Sync
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Vulnerabilidade' : 'Nova Vulnerabilidade'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Atualize os detalhes da vulnerabilidade' : 'Registe manualmente uma vulnerabilidade'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cve_id">CVE ID</Label>
                <Input
                  id="cve_id"
                  value={formData.cve_id}
                  onChange={(e) => setFormData({ ...formData, cve_id: e.target.value })}
                  placeholder="Ex: CVE-2024-1234"
                  data-testid="cve-id-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cvss_score">CVSS Score (0-10)</Label>
                <Input
                  id="cvss_score"
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  value={formData.cvss_score}
                  onChange={(e) => setFormData({ ...formData, cvss_score: e.target.value })}
                  placeholder="Ex: 7.5"
                  data-testid="cvss-score-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Vulnerabilidade RCE em OpenSSL"
                required
                data-testid="vuln-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="severity">Severidade</Label>
                <Select
                  value={formData.severity}
                  onValueChange={(v) => setFormData({ ...formData, severity: v })}
                >
                  <SelectTrigger id="severity" data-testid="vuln-severity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {severities.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="asset_id">Ativo Afetado</Label>
                <Select
                  value={formData.asset_id || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, asset_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger id="asset_id" data-testid="vuln-asset-select">
                    <SelectValue placeholder="Selecione um ativo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    {assets.map((a) => (
                      <SelectItem key={a.asset_id} value={a.asset_id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="affected_component">Componente Afetado</Label>
              <Input
                id="affected_component"
                value={formData.affected_component}
                onChange={(e) => setFormData({ ...formData, affected_component: e.target.value })}
                placeholder="Ex: openssl-1.1.1"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição técnica da vulnerabilidade..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="solution">Solução / Remediação</Label>
              <Textarea
                id="solution"
                rows={2}
                value={formData.solution}
                onChange={(e) => setFormData({ ...formData, solution: e.target.value })}
                placeholder="Ex: Atualizar para versão X.Y.Z"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={isSaving} data-testid="save-vulnerability-btn">
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editing ? 'Guardar' : 'Registar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Vulnerabilidade</AlertDialogTitle>
            <AlertDialogDescription>
              Tem a certeza que pretende eliminar "{selectedVuln?.title}"?
              Esta ação não pode ser revertida.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
